import { useTheme } from "@emotion/react";
import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material";
import TableAction from "app/components/TableAction";
import { H4 } from "app/components/Typography";
// STYLED COMPONENT
const StyledTable = styled(Table)(() => ({
  "& thead": {
    backgroundColor: "#f5f5f5",
    "& tr": {
      "& th": {
        paddingLeft: 10,
        paddingRight: 0,
        paddingTop: "10px",
        paddingBottom: "10px",
        minWidth: "150px"
      }
    }
  },
  "& tbody": {
    "& tr": {
      "& td": {
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 10,
        paddingBottom: 10,
        maxWidth: "200px",
        minWidth: "100px",
        overflow: "hidden" /* hide overflowed text */,
        whiteSpace: "nowrap",
        textOverflow: "ellipsis"
      },
      "& td:hover": {
        overflow: "visible",
        whiteSpace: "normal",
        backgroundColor: "inherit",
        position: "relative",
        zIndex: 10
      }
    }
  },
  width: "100%",
  tableLayout: "fixed", // <-- important for equal width
  overflow: "scroll"
}));

// columnHeaders can now be array of { label, width } or just string
export default function PaginationTableAlt({
  data,
  count,
  sortOrder,
  columnHeaders,
  columnSort,
  currentPage,
  rowsPerPage,
  rowsPerPageOptions = [5, 15, 25, 50],
  handleChangePage,
  handleChangeRowsPerPage,
  handleRowClick,
  handleSortOrder,
  actions,
  rowHover = false,
  tooltipLabel
}) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box overflow="auto">
      <StyledTable>
        <TableHead>
          <TableRow>
            {columnHeaders.map((header, index) => {
              const label = typeof header === "string" ? header : header.label;
              const width = typeof header === "string" ? undefined : header.width;
              const align = typeof header === "string" ? "left" : header.align || "left";
              return (
                <TableCell
                  key={`${label}_${index}`}
                  align={align}
                  style={{
                    fontWeight: "bold",
                    width: width || undefined,
                    minWidth: width || undefined
                  }}
                >
                  {label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {data
            .slice(0 * rowsPerPage, 0 * rowsPerPage + rowsPerPage)
            .map((currentRow, rowIndex) => {
              const rowContent = (
                <TableRow
                  key={currentRow.jp_id}
                  onClick={() => handleRowClick(currentRow)}
                  sx={
                    rowHover
                      ? {
                          cursor: "pointer",
                          transition: "background 0.2s",
                          "&:hover": {
                            backgroundColor: "#f3f4fa"
                          }
                        }
                      : {}
                  }
                >
                  {Object.values(currentRow).map((value, colIndex) => {
                    if (colIndex === 0 || colIndex === 7) {
                      // we do not want to show the row id or jp_is_applied value
                      return null;
                    }
                    // Get width for this column if provided
                    const header = columnHeaders[colIndex - 1];
                    const width = typeof header === "string" ? undefined : header?.width;
                    const align = typeof header === "string" ? "left" : header?.align || "left";
                    return (
                      <TableCell
                        key={`${value}_${colIndex}`}
                        align={align}
                        sx={{
                          backgroundColor: "transparent",
                          width: width || undefined,
                          minWidth: width || undefined
                        }}
                      >
                        <Typography
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {value ? value.toString() : ""}
                        </Typography>
                      </TableCell>
                    );
                  })}

                  {actions &&
                    actions.map((action, index) => {
                      return (
                        <TableCell key={`${action}_${index}`} align="center">
                          {action.text === "Apply" && currentRow.jp_is_applied ? (
                            <H4
                              sx={{
                                color: "#5a8da1",
                                backgroundColor: "lightblue",
                                padding: "4px 6px 4px 6px",
                                borderRadius: "5px",
                                fontWeight: "bold",
                                width: "100%",
                                textAlign: "center"
                              }}
                              mx={"auto"}
                            >
                              Applied
                            </H4>
                          ) : (
                            <TableAction
                              key={`${currentRow.jp_id}_${index}`}
                              icon={action.icon}
                              text={action.text && !isSmallScreen ? action.text : ""}
                              variant={action.variant || ""}
                              style={action.style || {}}
                              color={action.color}
                              size={action.size || "medium"}
                              action={(event) => {
                                event.stopPropagation();
                                action.action(currentRow, action.id);
                              }}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                </TableRow>
              );
              return tooltipLabel ? (
                <Tooltip key={`tooltip_${rowIndex}`} title={tooltipLabel} placement="bottom" arrow>
                  {rowContent}
                </Tooltip>
              ) : (
                rowContent
              );
            })}
        </TableBody>
      </StyledTable>

      <TablePagination
        sx={{ px: 2 }}
        page={currentPage}
        component="div"
        rowsPerPage={rowsPerPage}
        count={count}
        onPageChange={handleChangePage}
        rowsPerPageOptions={rowsPerPageOptions}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}
